import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Approach", to: "/approach" },
  { label: "Capabilities", to: "/capabilities" },
  { label: "Experience", to: "/experience" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Wordmark = ({ dark = false }) => (
  <span className="flex items-center gap-2.5">
    <span className="block h-2 w-2 bg-accent" aria-hidden="true" />
    <span
      className={`text-[13px] font-semibold tracking-[0.22em] ${
        dark ? "text-white" : "text-ink"
      }`}
    >
      IMPROVE
      <span className={`mx-1.5 font-normal ${dark ? "text-slate-500" : "text-slate-400"}`}>/</span>
      SYSTEMS
    </span>
  </span>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="nav-main"
      className="fixed inset-x-0 top-0 z-50 border-b border-line bg-canvas/90 backdrop-blur-md"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <Link to="/" data-testid="nav-logo" aria-label="Improve Systems home">
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors duration-200 ${
                  isActive ? "font-medium text-ink" : "text-slate-500 hover:text-ink"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-discuss"
            className="group hidden items-center gap-1.5 border border-ink px-4 py-2 text-[13px] tracking-wide text-ink transition-colors duration-300 hover:bg-navy hover:text-white sm:inline-flex"
          >
            Discuss a System
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="inline-flex h-10 w-10 items-center justify-center border border-line text-ink lg:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="border-t border-line bg-canvas px-6 py-6 lg:hidden"
        >
          <nav className="flex flex-col gap-4" aria-label="Mobile">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "font-medium text-ink" : "text-slate-500"}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta-discuss"
              className="mt-2 inline-flex w-fit items-center gap-1.5 border border-ink px-5 py-3 text-sm tracking-wide"
            >
              Discuss a System
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
